/**
 * Sign in page.
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import './sign-in.scss';

export function SignIn() {
  const navigate = useNavigate();
  const { signIn, isAuthenticated, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      console.log('Starting sign-in...');
      await signIn(email, password);
      console.log('✅ Sign-in successful');
      
      // AuthContext handles state updates via onAuthStateChange
      // The useEffect above will redirect to dashboard once isAuthenticated becomes true
      
      // Don't set isLoading to false - let redirect happen
      // The form will unmount when we navigate away
    } catch (err) {
      console.error('❌ Sign-in error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      setIsLoading(false);
    }
  };

  return (
    <div className="sign-in">
      <Card className="sign-in__card">
        <div className="sign-in__header">
          <h1 className="h1 brand brand--spark">Origyns</h1>
          <p className="of-text-muted">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="sign-in__form">
          {error && (
            <div className="sign-in__error">{error}</div>
          )}

          <div className="sign-in__field">
            <label htmlFor="email" className="label">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              required
              disabled={isLoading}
            />
          </div>

          <div className="sign-in__field">
            <label htmlFor="password" className="label">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              required
              disabled={isLoading}
            />
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign in'}
          </Button>

          <div style={{ textAlign: 'center', marginTop: 'var(--of-space-4)' }}>
            <p className="of-text-muted">
              Don't have an account?{' '}
              <Link 
                to="/auth/sign-up" 
                style={{ color: 'var(--of-flow-blue)', textDecoration: 'none' }}
              >
                Sign up
              </Link>
            </p>
          </div>
        </form>
      </Card>
    </div>
  );
}

